/**
 * Country filter menu: Search countries, All countries minus/check,
 * ISO-2 on the right, checkboxes. Not a ToolbarIconDropdown (those menus
 * close on item click; Email/Export must keep that).
 *
 * Portal z-index 10000, Escape, click-outside — same as Status filter.
 * Wired on Target Investors (7B), Growth Region, and Admin Investors Country (7C).
 */
import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckIcon, MinusIcon } from '@heroicons/react/24/solid';
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import {
  toolbarDropdownPanelPortaled,
  toolbarStandaloneActive,
  toolbarStandaloneBase,
  toolbarStandaloneInactive,
} from './listToolbarTokens';
import { formatCountryAbbreviation } from '../../utils/countryDisplay';
import {
  getAllCountriesCheckState,
  getCountryFilterOptionValues,
  getCountryFilterTriggerLabel,
  isCountryFilterActive,
  toggleAllCountries,
  toggleCountryFilterValue,
  type AllCountriesCheckState,
  type TargetCountryFilterOption,
  type TargetCountryFilterState,
} from '../../utils/targetCountryFilter';
import { TARGET_INVESTOR_FIELD_FILTER_NOT_SET } from '../../utils/targetInvestorFields';

export type TargetInvestorCountryFilterProps = {
  'aria-label'?: string;
  options: TargetCountryFilterOption[];
  value: TargetCountryFilterState;
  onChange: (next: TargetCountryFilterState) => void;
  disabled?: boolean;
  menuAlign?: 'left' | 'right';
  className?: string;
  triggerClassName?: string;
  title?: string;
  idleTriggerLabel?: string;
};

const MENU_MARGIN = 8;
const MENU_GAP = 4;
const MENU_FALLBACK_WIDTH = 288;

const checkBoxClass = (state: AllCountriesCheckState | 'checked' | 'unchecked') =>
  `flex h-4 w-4 shrink-0 items-center justify-center rounded ${
    state === 'unchecked'
      ? 'border border-primary-500/30 bg-white'
      : 'bg-primary-500 text-white'
  }`;

const CountryCheckMark: React.FC<{ state: AllCountriesCheckState | 'checked' | 'unchecked' }> = ({
  state,
}) => (
  <span aria-hidden className={checkBoxClass(state)}>
    {state === 'checked' ? <CheckIcon className="h-3 w-3" /> : null}
    {state === 'indeterminate' ? <MinusIcon className="h-3 w-3" /> : null}
  </span>
);

const isoForOption = (value: string): string => {
  if (value === TARGET_INVESTOR_FIELD_FILTER_NOT_SET) return '';
  const iso = formatCountryAbbreviation(value);
  return iso && iso !== value && iso.length === 2 ? iso : '';
};

const matchesCountryQuery = (option: TargetCountryFilterOption, query: string): boolean => {
  if (!query) return true;
  const iso = isoForOption(option.value);
  const haystack = `${option.label} ${iso} ${option.value}`.toLowerCase();
  return haystack.includes(query);
};

const TargetInvestorCountryFilter: React.FC<TargetInvestorCountryFilterProps> = ({
  'aria-label': ariaLabel = 'Filter by country',
  options,
  value,
  onChange,
  disabled = false,
  menuAlign = 'left',
  className = '',
  triggerClassName = '',
  title,
  idleTriggerLabel = 'Country',
}) => {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);

  const optionValues = useMemo(() => getCountryFilterOptionValues(options), [options]);
  const isActive = isCountryFilterActive(value);
  const triggerLabel = getCountryFilterTriggerLabel(value, options, idleTriggerLabel);
  const allCheck = getAllCountriesCheckState(value);
  const selected = new Set(value.all ? optionValues : value.selectedKeys);

  const menuOptions = useMemo<TargetCountryFilterOption[]>(
    () => [{ value: TARGET_INVESTOR_FIELD_FILTER_NOT_SET, label: 'Not set' }, ...options],
    [options]
  );

  const filteredOptions = useMemo(() => {
    const normalised = query.trim().toLowerCase();
    return menuOptions.filter((option) => matchesCountryQuery(option, normalised));
  }, [menuOptions, query]);

  const placeMenu = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const panelWidth = menuRef.current?.offsetWidth || MENU_FALLBACK_WIDTH;
    let left = menuAlign === 'right' ? rect.right - panelWidth : rect.left;
    if (left < MENU_MARGIN) left = MENU_MARGIN;
    if (left + panelWidth > window.innerWidth - MENU_MARGIN) {
      left = window.innerWidth - panelWidth - MENU_MARGIN;
    }
    setMenuPos({ top: rect.bottom + MENU_GAP, left });
  }, [menuAlign]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    placeMenu();
  }, [open, placeMenu, value.selectedKeys.length, value.all, query]);

  useEffect(() => {
    if (!open) return;
    searchRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
      setQuery('');
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        setQuery('');
        triggerRef.current?.focus();
      }
    };

    window.addEventListener('resize', placeMenu);
    window.addEventListener('scroll', placeMenu, true);
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('resize', placeMenu);
      window.removeEventListener('scroll', placeMenu, true);
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, placeMenu]);

  const handleTriggerClick = () => {
    if (disabled) return;
    setOpen((prev) => {
      if (prev) setQuery('');
      return !prev;
    });
  };

  const menu =
    open && menuPos
      ? createPortal(
          <div
            ref={menuRef}
            role="menu"
            data-menu-align={menuAlign}
            aria-label="Country filter"
            style={{
              position: 'fixed',
              top: menuPos.top,
              left: menuPos.left,
              zIndex: 10000,
            }}
            className={`${toolbarDropdownPanelPortaled} min-w-[18rem] w-[18rem] max-w-[18rem] py-0`}
          >
            <div className="flex items-center gap-1.5 border-b border-primary-500/10 px-3 py-2">
              <MagnifyingGlassIcon
                className="h-4 w-4 shrink-0 text-primary-500/50"
                aria-hidden
              />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search countries"
                className="min-w-0 flex-1 bg-transparent text-sm text-neutral-800 placeholder:text-neutral-400 focus:outline-none"
                aria-label="Search countries"
                aria-controls={listId}
              />
            </div>

            <div id={listId} className="max-h-[18rem] overflow-y-auto py-1">
              {!query.trim() ? (
                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={
                    allCheck === 'indeterminate' ? 'mixed' : allCheck === 'checked'
                  }
                  onClick={() => onChange(toggleAllCountries(value, optionValues))}
                  className={`flex w-full items-center justify-between px-3 py-2 text-sm font-semibold ${
                    allCheck === 'unchecked'
                      ? 'text-neutral-800 hover:bg-neutral-50'
                      : 'bg-primary-50/70 text-primary-700'
                  }`}
                >
                  <span>All countries</span>
                  <CountryCheckMark state={allCheck} />
                </button>
              ) : null}

              {filteredOptions.length === 0 ? (
                <p className="px-3 py-2 text-sm text-neutral-500">No matches</p>
              ) : (
                filteredOptions.map((option) => {
                  const checked = selected.has(option.value);
                  const iso = isoForOption(option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="menuitemcheckbox"
                      aria-checked={checked}
                      title={option.label}
                      onClick={() =>
                        onChange(toggleCountryFilterValue(value, option.value, optionValues))
                      }
                      className={`flex w-full items-center gap-2 px-3 py-1.5 text-sm ${
                        checked
                          ? 'bg-primary-50/70 font-medium text-primary-700'
                          : 'font-medium text-neutral-800 hover:bg-neutral-50'
                      }`}
                    >
                      <span className="min-w-0 flex-1 truncate text-left">{option.label}</span>
                      {iso ? (
                        <span className="shrink-0 tabular-nums text-neutral-500">{iso}</span>
                      ) : null}
                      <CountryCheckMark state={checked ? 'checked' : 'unchecked'} />
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div ref={rootRef} className={`relative z-20 ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleTriggerClick}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="menu"
        title={title}
        disabled={disabled}
        className={`${toolbarStandaloneBase} justify-between gap-1.5 ${
          isActive ? toolbarStandaloneActive : toolbarStandaloneInactive
        } ${disabled ? 'opacity-70 cursor-not-allowed' : ''} ${triggerClassName}`}
      >
        <span className="truncate font-semibold">{triggerLabel}</span>
        <ChevronDownIcon
          className={`w-3.5 h-3.5 shrink-0 text-primary-400 transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`}
          aria-hidden
        />
      </button>
      {menu}
    </div>
  );
};

export default TargetInvestorCountryFilter;
